import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { Task } from "@common/types/task";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useUpdateTask } from "@api/task.api";
import { useWorkspaceTaskGroups } from "@api/taskGroup.api";
import { CaptureClicks } from "@design-system/CaptureClicks/CaptureClicks";
import { memo, useMemo } from "react";
import { TaskGroupSelect } from "@components/drawers/TaskGroupSelect";
import { Picker } from "@design-system/Picker/Picker.lib";
import { NestedTaskRow } from "../../types";

export const GroupCell = memo(
    ({
        row,
    }: CellContext<
        ViewTableFeatures,
        NestedTaskRow,
        Task["groupId"] | null
    >) => {
        const task = row.original as Task;
        const updateTask = useUpdateTask(task);
        const { data: taskGroups = [] } = useWorkspaceTaskGroups();

        const taskGroupName = useMemo(
            () =>
                taskGroups.find((taskGroup) => taskGroup.id === task.groupId)
                    ?.name,
            [task.groupId, taskGroups],
        );

        const trigger = useMemo(
            () => (
                <Picker.CellContent
                    addText="Add group"
                    hideAddIcon
                    value={task.groupId ? (taskGroupName ?? "") : undefined}
                />
            ),
            [task.groupId, taskGroupName],
        );

        if (row.original.isGroup || row.original.isNewPlaceholder) {
            return null;
        }

        return (
            <ViewTableCell>
                <CaptureClicks>
                    <TaskGroupSelect
                        value={task.groupId ?? null}
                        onChange={(groupId) =>
                            updateTask.mutate({
                                groupId,
                            })
                        }
                        trigger={trigger}
                    />
                </CaptureClicks>
            </ViewTableCell>
        );
    },
);
